import { Survey } from "../types";
import getService from "../../Projects/CreateProjectMspa/Services/Questionnaire/api/getService";
import { getResourceName } from "../../Projects/ServicesV2/common";

const formatRecommendations = async (
  recommendations,
  projectResources,
  authorizationCode,
  accountSlug,
  handleFailResponse,
  resourceRates,
  resourceRatesEnabled,
  approved = false
) => {
  const formattedRecommendations: Survey["recommendations"] = [];

  await Promise.all(
    recommendations.map(async (recommendation) => {
      if (recommendation["item_type"] === "Task") {
        const { data, included, error, notApproved } = await getService({
          account: { authorizationCode, slug: accountSlug },
          serviceId: recommendation["item_id"],
          approved,
        });

        if (notApproved) return;

        if (error) {
          handleFailResponse("");
          return;
        }

        const projectResourceId = recommendation?.["project_resource_id"];
        const projectResource = projectResourceId
          ? projectResources?.find((i) => i.id == projectResourceId)
          : undefined;

        const phaseId = data?.relationships?.phase?.data?.id;
        const phase = phaseId
          ? included?.find((i) => i.type === "phases" && i.id == phaseId)
          : undefined;

        const categoryId = data?.relationships?.["service-category"]?.data?.id;
        const category = categoryId
          ? included?.find(
              (i) => i.type === "service-categories" && i.id == categoryId
            )
          : undefined;
        const categoryLOB = category?.attributes?.["lob-id"];
        const serviceLOB = data?.attributes?.["lob-id"];

        const resourceId = data?.relationships?.resource?.data?.id;
        const filteredResourceRates =
          resourceRatesEnabled && resourceId
            ? resourceRates.filter(
                (rr) => rr?.relationships?.resource?.data?.id == resourceId
              )
            : [];

        const resourceRateForServiceCategory = categoryLOB
          ? filteredResourceRates.find(
              (rr) =>
                rr?.relationships?.["line-of-business"]?.data?.id == categoryLOB
            )
          : undefined;
        const resourceRateForServiceLOB = serviceLOB
          ? filteredResourceRates.find(
              (rr) =>
                rr?.relationships?.["line-of-business"]?.data?.id == serviceLOB
            )
          : undefined;

        const resource =
          //If there is a project-resource already assigned, use that;
          projectResource ||
          // Else if there’s a resource rate for the resource aligned to the service category for the service, then use that rate;
          resourceRateForServiceCategory ||
          // Else, if there’s a resource rate for the resource aligned to the lob of the service, then use that rate;
          resourceRateForServiceLOB ||
          // Else, if there’s a resource rate for the resource aligned to NO lob/SC then use that rate.
          filteredResourceRates?.[0] ||
          // Else, if project-resource-rate setting is off, use standard resource, otherwise leave blank
          !resourceRatesEnabled
            ? data?.included?.find(
                (i) => i.type === "resources" && i.id == resourceId
              )
            : undefined;

        //Prioritize any assigned project-resource. If none then use service resource
        const recommendationResource = projectResource
          ? {
              id: projectResource.id,
              name: getResourceName(projectResource),
            }
          : resource
          ? {
              id: `${resource?.type}-${resource?.id}`,
              name: getResourceName(resource),
            }
          : undefined;

        //All subservices associated with service
        const subservices =
          data?.relationships?.subservices?.data?.map((subservice) => {
            const sub = included?.find(
              (item) => item.type === "subservices" && item.id == subservice.id
            );
            const subResource = included?.find(
              (i) =>
                i.type === "resources" &&
                i.id == sub?.relationships?.resource?.data?.id
            );

            return {
              id: sub?.id || "",
              type: "subservices",
              name: sub?.attributes?.name || "",
              quantity: sub?.attributes?.quantity || "",
              resource: subResource
                ? {
                    id: `resources-${subResource?.id}`,
                    name: getResourceName(subResource),
                  }
                : recommendationResource,
              hours: subservice?.attributes?.["suggested-hours"],
              active: sub?.attributes?.active,
            };
          }) || [];

        //Recommendations for subservices
        const refinements =
          recommendation?.refinements?.map((refinement) => {
            const subservice = included?.find(
              (i) => i.type === "subservices" && i.id == refinement["item_id"]
            );
            if (!subservice) return null;

            const refinementProjectResourceId =
              refinement?.["project_resource_id"];
            const refinementProjectResource = refinementProjectResourceId
              ? projectResources?.find(
                  (i) => i.id == refinementProjectResourceId
                )
              : undefined;

            const resourceId =
              refinement?.["resource_id"] ||
              subservice?.relationships?.resource?.data?.id;
            const resource = resourceId
              ? included?.find(
                  (i) => i.type === "resources" && i.id == resourceId
                )
              : undefined;

            //Prioritize any assigned project-resource. If none then use subservice resource
            const refinementResource = refinementProjectResource
              ? {
                  id: refinementProjectResource.id,
                  name: getResourceName(refinementProjectResource),
                }
              : resource
              ? {
                  id: `resources-${resource?.id}`,
                  name: getResourceName(resource),
                }
              : undefined;

            return {
              id: refinement["item_id"],
              type: "subservices",
              serviceType:
                data?.attributes?.["service-type"] || "professional_services",
              name: subservice.attributes?.name || "",
              quantity: refinement?.quantity ?? (subservice.attributes?.quantity ?? 1),
              hours: subservice.attributes?.["suggested-hours"] || "",
              resource: refinementResource || recommendationResource,
              status: refinement?.status || "",
            };
          })
          .filter(Boolean) || [];

        const products = data?.relationships?.products?.data?.map((p) => {
          const product = included?.find(
            (item) => item.type === "products" && item.id == p.id
          );
          return {
            id: product?.id || 0,
            name: product?.attributes?.name || "",
            mpn: product?.attributes?.["manufacturer-part-number"] || "",
            price: product?.attributes?.["unit-price"] || "",
          };
        });

        formattedRecommendations.push({
          id: recommendation.id,
          type: "services",
          serviceType:
            data?.attributes?.["service-type"] || "professional_services",
          category:
            category?.attributes?.["nested-name"] || category?.attributes?.name,
          name: data?.attributes?.name,
          quantity:
            recommendation?.quantity || data?.attributes?.["minimum-quantity"],
          hours: data?.attributes?.["suggested-hours"],
          resource: recommendationResource,
          phase:
            data?.attributes?.["service-type"] === "professional_services"
              ? {
                  id: phase?.id || -1,
                  name: phase?.attributes?.name || "",
                  position: phase?.attributes?.position || 999999,
                }
              : {
                  id: -1,
                  name: "Managed Services",
                  position: 999999,
                },
          refinements,
          subservices: subservices.filter((sub) => sub.active),
          products,
          status: recommendation?.status || "",
        });
      }
    })
  );

  return formattedRecommendations;
};
export default formatRecommendations;
